import { gql } from '@apollo/client';

import {
  KOMMO_LEAD_CARD_FIELDS,
  KOMMO_LEAD_ROW_FIELDS,
  KOMMO_NOTE_FIELDS,
  KOMMO_STAGE_FIELDS,
  KOMMO_SYNC_STATUS_FIELDS,
  KOMMO_USER_FIELDS,
  LEAD_CONVERSATION_FIELDS,
  LEAD_LADDER_RUN_FIELDS,
  LEAD_SMS_STATE_FIELDS,
} from '../fragments/crm';
import { SMS_PREVIEW_FIELDS } from '../fragments/sms';

/**
 * ТЗ 0084 — лиды Kommo в ERP. Имена операций намеренно отличаются от полей
 * бэкенда (`kommoLeads` → `KommoLeadList` и т. д.): иначе codegen склеил бы
 * тип результата с типом поля.
 */

/** Список лидов по фильтрам: страница, всего, источник, последняя синхронизация. */
export const KommoLeadListQuery = gql`
  ${KOMMO_LEAD_ROW_FIELDS}
  query KommoLeadList(
    $statusIds: [ID!]
    $responsible: String
    $search: String
    $includeClosed: Boolean
    $ladder: String
    $utmSource: [String!]
    $utmMedium: [String!]
    $utmCampaign: [String!]
    $fundingStatus: [String!]
    $hasFunding: Boolean
    $hasStudent: Boolean
    $sort: String
    $offset: Int
    $limit: Int
  ) {
    kommoLeads(
      statusIds: $statusIds
      responsible: $responsible
      search: $search
      includeClosed: $includeClosed
      ladder: $ladder
      utmSource: $utmSource
      utmMedium: $utmMedium
      utmCampaign: $utmCampaign
      fundingStatus: $fundingStatus
      hasFunding: $hasFunding
      hasStudent: $hasStudent
      sort: $sort
      offset: $offset
      limit: $limit
    ) {
      items {
        ...KommoLeadRowFields
      }
      total
      source
      syncedAt
    }
  }
`;

/**
 * ТЗ 0086 — значения меток и этапов дофинансирования со счётчиками по ВСЕМУ
 * зеркалу (а не по отфильтрованной ленте): после выбора значения варианты
 * фильтра не схлопываются до него одного. `value: ''` — «без метки».
 */
export const KommoLeadFacetsQuery = gql`
  query KommoLeadFacets($includeClosed: Boolean) {
    kommoLeadFacets(includeClosed: $includeClosed) {
      utmSource {
        value
        count
      }
      utmMedium {
        value
        count
      }
      utmCampaign {
        value
        count
      }
      fundingStatus {
        value
        count
      }
      withFunding
      withStudent
    }
  }
`;

/** Этапы воронки «New leads» и число открытых лидов на каждом — чипы фильтра. */
export const KommoLeadStageListQuery = gql`
  ${KOMMO_STAGE_FIELDS}
  query KommoLeadStageList {
    kommoLeadStages {
      ...KommoStageFields
      leadsCount
    }
  }
`;

/** Карточка лида. */
export const KommoLeadCardQuery = gql`
  ${KOMMO_LEAD_CARD_FIELDS}
  query KommoLeadCard($id: ID!) {
    kommoLead(id: $id) {
      ...KommoLeadCardFields
    }
  }
`;

/**
 * ТЗ 0090 — лид договора в карточке договора: только то, что нужно блоку
 * лесенок (можно ли запускать и почему лесенка не пошлёт). Та же запись кэша
 * `KommoLeadType:<id>`, что у карточки лида: действия с лесенками
 * перечитывают карточку лида, и блок обновляется сам.
 */
export const ContractLeadLadderHeadQuery = gql`
  query ContractLeadLadderHead($id: ID!) {
    kommoLead(id: $id) {
      id
      personName
      isClosed
      inPipeline
      isDeleted
      ladderReadiness {
        kind
        warning
        firstSms
        firstSmsSegments
      }
    }
  }
`;

/**
 * Примечания Kommo (вживую, кэш сервера 60 с) и наши, ещё не записанные.
 * Kommo недоступен — `error`, а наши примечания приходят всё равно.
 */
export const KommoLeadNoteListQuery = gql`
  ${KOMMO_NOTE_FIELDS}
  query KommoLeadNoteList($leadId: ID!) {
    kommoLeadNotes(leadId: $leadId) {
      items {
        ...KommoNoteFields
      }
      error {
        code
        message
      }
    }
  }
`;

/** Журнал карточки лида: SMS ушла / не ушла, примечание не записалось. */
export const KommoLeadEventListQuery = gql`
  query KommoLeadEventList($leadId: ID!) {
    kommoLeadEvents(leadId: $leadId) {
      id
      kind
      actorName
      createdAt
      messageId
      code
    }
  }
`;

/** SMS в карточке лида: можно ли, куда уйдёт, история. */
export const LeadSmsStateQuery = gql`
  ${LEAD_SMS_STATE_FIELDS}
  query LeadSmsState($leadId: ID!) {
    kommoLeadSms(leadId: $leadId) {
      ...LeadSmsStateFields
    }
  }
`;

/** Живой предпросмотр SMS лиду. */
export const LeadSmsPreviewQuery = gql`
  ${SMS_PREVIEW_FIELDS}
  query LeadSmsPreview($leadId: ID!, $text: String!, $link: LeadSmsLinkInput) {
    previewLeadSms(leadId: $leadId, text: $text, link: $link) {
      ...SmsPreviewFields
    }
  }
`;

/** Этап 2: запуски SMS-лесенок лида (последние 10), новые сверху. */
export const KommoLeadLadderListQuery = gql`
  ${LEAD_LADDER_RUN_FIELDS}
  query KommoLeadLadderList($leadId: ID!) {
    kommoLeadLadders(leadId: $leadId) {
      ...LeadLadderRunFields
    }
  }
`;

/** Этап 3: треды Gmail с лидом — лента «Переписки» карточки. */
export const KommoLeadThreadListQuery = gql`
  ${LEAD_CONVERSATION_FIELDS}
  query KommoLeadThreadList($leadId: ID!) {
    kommoLeadConversations(leadId: $leadId) {
      ...LeadConversationFields
    }
  }
`;

/** Admin: пользователи Kommo и сотрудники ERP за ними. */
export const KommoUserListQuery = gql`
  ${KOMMO_USER_FIELDS}
  query KommoUserList {
    kommoUsers {
      ...KommoUserFields
    }
  }
`;

/** Состояние синхронизации (прогоны и ошибка — только Admin). */
export const KommoSyncStateQuery = gql`
  ${KOMMO_SYNC_STATUS_FIELDS}
  query KommoSyncState {
    kommoSyncStatus {
      ...KommoSyncStatusFields
    }
  }
`;

/** SMS в карточке кандидата дофинансирования. */
export const CandidateSmsStateQuery = gql`
  ${LEAD_SMS_STATE_FIELDS}
  query CandidateSmsState($candidateId: ID!) {
    fundingCandidateSms(candidateId: $candidateId) {
      ...LeadSmsStateFields
    }
  }
`;

/** Живой предпросмотр SMS кандидату дофинансирования. */
export const CandidateSmsPreviewQuery = gql`
  ${SMS_PREVIEW_FIELDS}
  query CandidateSmsPreview(
    $candidateId: ID!
    $text: String!
    $link: LeadSmsLinkInput
  ) {
    previewFundingCandidateSms(
      candidateId: $candidateId
      text: $text
      link: $link
    ) {
      ...SmsPreviewFields
    }
  }
`;

/**
 * Задача #116: лиды в палитре команд ⌘K. Переменная называется `$query` —
 * как у остальных пяти поисков палитры: по `variables.query` палитра
 * отбрасывает ответы на прошлое нажатие клавиши. Закрытые лиды тоже ищутся:
 * человека ищут по имени или телефону, не зная, на каком он этапе.
 */
export const PaletteLeadsQuery = gql`
  query PaletteLeads($query: String) {
    kommoLeads(search: $query, includeClosed: true, limit: 5) {
      items {
        id
        name
        personName
        phone
        email
        isClosed
        stage {
          id
          name
        }
      }
    }
  }
`;
